import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/utils/formatters';
import { TrendingUp } from 'lucide-react';

const FinancialOverview = ({ financialData }) => {
  if (!financialData) {
    return <div>Загрузка...</div>;
  }

  const { revenue, expenses, margin, payments, projects_with_data } = financialData;

  // Цвет маржи
  const marginPct = margin.percent || 0;
  let marginColor = 'text-red-600';
  let marginBg = 'from-red-50 to-orange-50 border-red-200';
  if (marginPct >= 15) {
    marginColor = 'text-green-600';
    marginBg = 'from-green-50 to-emerald-50 border-green-200';
  } else if (marginPct >= 5) {
    marginColor = 'text-yellow-600';
    marginBg = 'from-yellow-50 to-amber-50 border-yellow-200';
  }

  const hasMarginData = margin.amount !== 0 || margin.percent !== 0;

  return (
    <Card data-testid="financial-overview-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>💰</span> Финансы
          {projects_with_data > 0 && (
            <span className="text-xs font-normal text-gray-400 ml-auto">
              {projects_with_data} проектов с данными
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Маржинальность — главный KPI */}
          {hasMarginData ? (
            <div
              className={`p-4 bg-gradient-to-r ${marginBg} rounded-lg border`}
              data-testid="total-margin"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600">Маржа (портфель)</div>
                  <div className={`text-3xl font-bold ${marginColor}`}>
                    {marginPct}%
                  </div>
                </div>
                <TrendingUp className={`w-8 h-8 ${marginColor}`} />
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {formatCurrency(margin.amount)}
              </div>
              {margin.gross_profit !== 0 && (
                <div className="mt-0.5 text-xs text-gray-500">
                  Валовая прибыль: {formatCurrency(margin.gross_profit)}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-400 text-sm">
              Нет данных по маржинальности
            </div>
          )}

          {/* Выручка */}
          <div className="pt-3 border-t">
            <h4 className="text-sm font-semibold mb-2">Выручка</h4>
            <div className="space-y-1.5">
              <Row label="КП с НДС" value={revenue.kp} />
              <Row label="Договоры с НДС" value={revenue.contract} />
            </div>
          </div>

          {/* Затраты */}
          <div className="pt-3 border-t">
            <h4 className="text-sm font-semibold mb-2">Затраты</h4>
            <div className="space-y-1.5">
              <Row label="Итого затрат" value={expenses.total} red />
              <Row label="Субподряд" value={expenses.subcontract} />
              <Row label="Налоги" value={expenses.taxes} />
            </div>
          </div>

          {/* Оплаты (если есть) */}
          {(payments.vendor_fact || 0) > 0 && (
            <div className="pt-3 border-t">
              <h4 className="text-sm font-semibold mb-2">Оплаты (факт)</h4>
              <Row label="Вендору" value={payments.vendor_fact} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/** Строка label — value */
const Row = ({ label, value, red }) => {
  const hasValue = value !== 0 && value != null;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className={`font-semibold ${red && hasValue ? 'text-red-600' : ''}`}>
        {hasValue ? formatCurrency(value) : '—'}
      </span>
    </div>
  );
};

export default FinancialOverview;
